import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Icon } from '@/components/icon/index.js';
import { Button } from '../components/design-system/primitives/Button/Button.js';
import { Card } from '../components/design-system/primitives/Card/Card.js';
import { Input } from '../components/design-system/primitives/Input/Input.js';
import { SEO } from '../components/seo/index.js';
import { getAuthFlowErrorMessage } from '../lib/authFlowErrors.js';
import { legalPathForLanguage } from '../lib/legalPaths.js';
import { consumePostAuthRedirect } from '../lib/postAuthRedirect.js';
import { useAuthStore } from '../store/authStore.js';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const signInWithEmail = useAuthStore((s) => s.signInWithEmail);
  const sendPasswordReset = useAuthStore((s) => s.sendPasswordReset);
  const verifiedToastShown = useRef(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState<string | null>(null);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const termsPath = legalPathForLanguage(i18n.language, '/terms');
  const privacyPath = legalPathForLanguage(i18n.language, '/privacy');

  useEffect(() => {
    if (verifiedToastShown.current) return;
    if (searchParams.get('verified') !== '1') return;
    verifiedToastShown.current = true;
    toast.success(t('auth.login.verifiedSuccess'));
    navigate('/login', { replace: true });
  }, [navigate, searchParams, t]);

  useEffect(() => {
    if (isInitialized && user) {
      navigate(consumePostAuthRedirect(), { replace: true });
    }
  }, [isInitialized, user, navigate]);

  const onGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      setError(getAuthFlowErrorMessage(err, t, 'login'));
    } finally {
      setLoading(false);
    }
  };

  const onEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setResetMsg(null);
    const normalizedEmail = email.trim();
    if (!emailValid) {
      setError(t('auth.login.errors.invalidEmail'));
      return;
    }
    if (!password) {
      setError(t('auth.login.errors.signInFailed'));
      return;
    }
    setLoading(true);
    try {
      await signInWithEmail(normalizedEmail, password);
      navigate(consumePostAuthRedirect(), { replace: true });
    } catch (err: unknown) {
      const code =
        err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : '';
      if (code === 'auth/user-not-found') setError(t('auth.login.errors.userNotFound'));
      else if (code === 'auth/wrong-password' || code === 'auth/invalid-credential')
        setError(t('auth.login.errors.wrongPassword'));
      else setError(t('auth.login.errors.signInFailed'));
    } finally {
      setLoading(false);
    }
  };

  const onForgot = async () => {
    const normalizedEmail = email.trim();
    setError(null);
    if (!normalizedEmail) {
      setError(t('auth.login.errors.enterEmailFirst'));
      return;
    }
    if (!emailValid) {
      setError(t('auth.login.errors.invalidEmail'));
      return;
    }
    setResetMsg(null);
    try {
      await sendPasswordReset(normalizedEmail);
      setResetMsg(t('auth.login.errors.resetSent'));
    } catch {
      setError(t('auth.login.errors.resetFailed'));
    }
  };

  return (
    <>
      <SEO
        title={t('auth.signInTitle', { defaultValue: 'Sign in' })}
        description={t('auth.login.metaDescription', { defaultValue: 'Sign in to InterviewGator and continue your interview practice sessions.' })}
      />
      <Card variant="outlined" className="w-full rounded-lg !border-0 bg-ig-bg-primary p-6 md:p-8">
        <div className="mb-6">
          <p className="mb-3 font-sans text-[11px] uppercase tracking-[0.136em] text-ig-accent">{t('auth.login.eyebrow')}</p>
          <h1 className="font-display text-[32px] font-normal leading-tight text-ig-text-primary">{t('auth.login.title')}</h1>
        </div>

        <form onSubmit={onEmail} noValidate className="space-y-4">
          <Input
            id="login-v2-email"
            type="email"
            label={t('auth.login.email')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            inputMode="email"
            placeholder={t('auth.login.emailPlaceholder')}
            className="h-12 rounded-md !border-0 bg-ig-bg-tertiary"
          />

          <div className="pb-3">
            <Input
              id="login-v2-password"
              type={showPassword ? 'text' : 'password'}
              label={t('auth.login.password')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder={t('auth.login.passwordPlaceholder')}
              className="h-12 rounded-md !border-0 bg-ig-bg-tertiary pr-20"
            />
            <div className="-mt-9 flex justify-end pr-3">
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="text-[13px] text-ig-text-secondary hover:text-ig-text-primary"
              >
                {showPassword ? t('common.hide') : t('common.show')}
              </button>
            </div>
          </div>

          <div className="mt-2 flex justify-end">
            <button
              type="button"
              disabled={loading}
              onClick={() => void onForgot()}
              className="text-[13px] text-ig-accent hover:underline disabled:opacity-50"
            >
              {t('auth.login.forgot')}
            </button>
          </div>

          <Button
            type="button"
            variant="secondary"
            size="lg"
            loading={loading}
            onClick={() => void onGoogle()}
            leftIcon={<Icon name="google" className="h-5 w-5" />}
            className="w-full rounded-md !border-0"
          >
            {t('auth.login.continueWithGoogle')}
          </Button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-ig-border" />
            <span className="text-[12px] text-ig-text-tertiary">{t('auth.login.orSignInEmail')}</span>
            <div className="h-px flex-1 bg-ig-border" />
          </div>

          {error ? <p className="text-[13px] text-ig-danger">{error}</p> : null}
          {resetMsg ? <p className="text-[13px] text-ig-accent">{resetMsg}</p> : null}

          <Button type="submit" variant="primary" size="lg" loading={loading} className="w-full rounded-md">
            {t('auth.login.submit')}
          </Button>
        </form>

        <div className="mt-6 space-y-3 text-center">
          <p className="text-[14px] text-ig-text-secondary">
            {t('auth.login.noAccount')}{' '}
            <Link to="/register" className="text-ig-accent hover:underline">
              {t('auth.login.createOne')}
            </Link>
          </p>
          <p className="text-[12px] text-ig-text-tertiary">
            {t('auth.login.agreePrefix')}{' '}
            <Link to={termsPath} className="text-ig-text-secondary hover:underline">
              {t('auth.login.terms')}
            </Link>{' '}
            {t('auth.login.and')}{' '}
            <Link to={privacyPath} className="text-ig-text-secondary hover:underline">
              {t('auth.login.privacy')}
            </Link>
          </p>
        </div>
      </Card>
    </>
  );
}
